import { AssetStatus as AssetStatusEnum } from 'packages/types/assets';

export type AssetStatus = AssetStatusEnum.UPLOAD_IN_PROGRESS | AssetStatusEnum.COMPLETED | AssetStatusEnum.CANCELLED;

export type UploaderStages = 'validating' | 'gettingUploadUrl' | 'uploading' | 'uploaded' | 'statusUpdate';
export type UploaderErrorType = 'generic' | 'mimeType' | 'size' | 'resolution' | 'cancelled';

export type UploaderError = {
    status: 'error';
    stage: UploaderStages;
    type: UploaderErrorType;
};
type UploaderBaseState = {
    uploadingCount?: number;
    totalCount?: number;
    uploadsProgress?: Map<string, 'UPLOAD_IN_PROGRESS' | 'SUCCESS' | 'ERROR'>;
};

type UploaderProgressState<TStage extends UploaderStages, TFields extends object = {}> = {
    stage: TStage;
    status: 'inProgress';
    hideNotification?: boolean;
} & TFields;

export type UploaderProgress =
    | UploaderProgressState<'validating'>
    | UploaderProgressState<'gettingUploadUrl'>
    | UploaderProgressState<'statusUpdate', { value: AssetStatus; uploadData?: any }>
    | UploaderProgressState<'uploading', { total: number; loaded: number; percent: number }>
    | UploaderProgressState<'uploaded', { url: string }>;

export type Task<T> = UploaderError | { status: 'success'; data: T };

export type UploaderState =
    | ({ status: 'ready' } & UploaderBaseState)
    | (UploaderProgress & UploaderBaseState)
    | ({ status: 'success'; hideNotification?: boolean } & UploaderBaseState)
    | (UploaderError & UploaderBaseState);

export const ACCEPT = {
    image: [
        'image/jpeg',
        'image/heic',
        'image/heif',
        'image/avif',
        'image/bmp',
        'image/png',
        'image/webp',
        'image/tiff',
        'image/svg+xml',
        'application/eps',
        'application/x-eps',
        'image/eps',
        'image/x-eps',
        'application/postscript',
        'image/x-sony-arw',
        'image/x-panasonic-rw2',
        'image/x-adobe-dng',
        'image/x-olympus-orf',
        'image/x-canon-cr3',
        'image/x-nikon-nef',
    ],
    video: ['video/avi', 'video/mp4', 'video/quicktime', 'video/mpeg', 'video/webm'],
    audio: ['audio/aac', 'audio/aiff', 'audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/x-wav'],
    document: [
        '.sketch',
        'application/sketch',
        'image/vnd.adobe.photoshop',
        'image/psd',
        'application/psd',
        'application/photoshop',
        'application/x-photoshop',
    ],
    // Temporarily outside of document category due to validation requirements, will be moved back once fully supported
    idml: [
        '.idml',
        'application/vnd.adobe.indesign-idml-package',
        '.zip',
        'application/zip',
        'application/x-zip-compressed',
    ],

    get all() {
        return [...this.document, ...this.image, ...this.video, ...this.audio];
    },
};

export type AssetType = keyof typeof ACCEPT;

export type UploadService = (props: {
    file: File;
    customerId: number;
    collectionId?: number;
    abortSignal: AbortSignal;
    assetTypes: AssetType[];
    hideNotification?: boolean;
}) => AsyncGenerator<UploaderState, void, void>;
